import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Check, ChevronDown, Search } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from 'utils/cn'

export interface AsyncComboboxProps<T> {
  /** Currently selected item. `null` when nothing is selected. */
  value: T | null
  onChange: (item: T | null) => void
  /** Async loader — called with the debounced search query. Keep the result
   *  bounded (the server's default page size is 100). */
  load: (q: string) => Promise<T[]>
  /** Stable id for a result (used as React key + selection comparison). */
  getKey: (item: T) => string
  /** Label to render in the trigger and the list row. */
  getLabel: (item: T) => ReactNode
  /** Optional second-line subtext in the list row. */
  getSublabel?: (item: T) => ReactNode
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  /** Allow clearing back to null via a dedicated row. */
  clearable?: boolean
  clearLabel?: string
}

const DEFAULT_DEBOUNCE = 200
const ROW_HEIGHT = 52
const VIEWPORT_HEIGHT = 300

export function AsyncCombobox<T>({
  value,
  onChange,
  load,
  getKey,
  getLabel,
  getSublabel,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results',
  disabled,
  className,
  clearable,
  clearLabel = 'Clear selection',
}: AsyncComboboxProps<T>) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [debounced, setDebounced] = useState('')
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const fetchCountRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input), DEFAULT_DEBOUNCE)
    return () => clearTimeout(t)
  }, [input])

  useEffect(() => {
    if (!open) {
      return
    }
    const fetchId = ++fetchCountRef.current
    setLoading(true)
    load(debounced)
      .then((result) => {
        if (fetchId === fetchCountRef.current) {
          setItems(result)
        }
      })
      .finally(() => {
        if (fetchId === fetchCountRef.current) {
          setLoading(false)
        }
      })
  }, [open, debounced, load])

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  })

  const handleSelect = useCallback(
    (item: T) => {
      onChange(item)
      setOpen(false)
    },
    [onChange],
  )

  const triggerLabel = useMemo(() => (value ? getLabel(value) : placeholder), [value, getLabel, placeholder])
  const valueKey = value ? getKey(value) : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}>
          <span className={cn('line-clamp-1 text-left', !value && 'text-muted-foreground')}>{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}>
        <div className="flex flex-col">
          <div className="flex items-center border-b border-border px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          {clearable && value && (
            <button
              type="button"
              onClick={() => {
                onChange(null)
                setOpen(false)
              }}
              className="border-b border-border/50 px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent">
              {clearLabel}
            </button>
          )}
          <div ref={scrollRef} style={{ maxHeight: VIEWPORT_HEIGHT }} className="overflow-auto p-1">
            {loading && items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
            ) : (
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                {virtualizer.getVirtualItems().map((v) => {
                  const item = items[v.index]
                  const key = getKey(item)
                  const selected = key === valueKey
                  return (
                    <div
                      key={key}
                      role="option"
                      aria-selected={selected}
                      onClick={() => handleSelect(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleSelect(item)
                        }
                      }}
                      tabIndex={0}
                      className={cn(
                        'absolute left-0 right-0 flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm hover:bg-accent hover:text-accent-foreground',
                        selected && 'bg-accent text-accent-foreground',
                      )}
                      style={{ transform: `translateY(${v.start}px)`, height: ROW_HEIGHT }}>
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        {selected && <Check className="h-4 w-4" />}
                      </span>
                      <div className="flex flex-col overflow-hidden">
                        <span className="truncate">{getLabel(item)}</span>
                        {getSublabel && (
                          <span className="truncate text-xs text-muted-foreground">{getSublabel(item)}</span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
